import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Edit2, Save, Sparkles, Trash2, X, Plus, AlertTriangle, Archive, Ban, RotateCcw } from 'lucide-react';
import api from '../lib/api';
import ImageUploadField from '../components/ImageUploadField';
import HelpCallout from './HelpCallout';

const CATEGORIES = ['days_out', 'heritage', 'outdoors', 'food_drink', 'accommodation', 'holiday', 'event'];

// Human-readable labels for the admin dropdown — keep in sync with the
// public-facing labels in ExplorePage.js so editors see the same wording.
const CATEGORY_LABELS = {
  days_out: 'Days Out',
  heritage: 'Heritage',
  outdoors: 'Outdoors',
  food_drink: 'Places to Eat and Drink',
  accommodation: 'Stays',
  holiday: 'Holiday',
  event: 'Event',
};
const TIERS = ['', 'gold', 'silver', 'bronze'];

/**
 * Three-state toggle for accessibility fields: Yes / No / Unknown.
 * Critical that "Unknown" (null) is distinct from "No" (false) — claiming a
 * venue has NO hearing loop when we just don't know wastes a disabled
 * visitor's trip. Lives outside the parent component to satisfy React's
 * stable-component rule.
 */
function AccessTriToggle({ field, label, hint, value, onChange }) {
  const norm = value === true ? true : value === false ? false : null;
  const opts = [
    { v: true, label: 'Yes' },
    { v: false, label: 'No' },
    { v: null, label: '?' },
  ];
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-charcoal/5 last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-charcoal">{label}</div>
        {hint && <div className="text-xs text-muted mt-0.5">{hint}</div>}
      </div>
      <div className="inline-flex bg-admin border border-charcoal/15 rounded-full p-0.5 shrink-0" data-testid={`acc-tri-${field}`}>
        {opts.map((opt) => {
          const active = norm === opt.v;
          return (
            <button
              key={String(opt.v)}
              type="button"
              onClick={() => onChange(opt.v)}
              data-testid={`acc-tri-${field}-${opt.v === null ? 'unknown' : opt.v ? 'yes' : 'no'}`}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                active
                  ? (opt.v === true ? 'bg-forest text-base' : opt.v === false ? 'bg-charcoal text-base' : 'bg-charcoal/15 text-charcoal/70')
                  : 'text-charcoal/55 hover:text-charcoal'
              }`}
              aria-pressed={active}
              aria-label={`${label}: ${opt.label}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function emptyAttraction() {
  return {
    slug: '', name: '', category: 'days_out', summary: '', description: '',
    hero_image: '', town: '', address: '', postcode: '', phone: '', email: '', website: '',
    tripadvisor_url: '', tags: [], family_friendly: true, dog_friendly: false,
    free_entry: false, indoor: false, outdoor: false, accessible: false, evening_friendly: false,
    is_featured_member: false, member_tier: null, published: true, lat: null, lng: null,
    stayplayexplore: false, perfectbreak: false,
    open_days: [], season_open_from: null, season_open_to: null, opening_notes: '',
    dwell_minutes: null, dwell_source: null, dwell_confidence: null, dwell_needs_review: false,
    awards_won: [],
  };
}

// Public display limits per membership level — keep in sync with
// TIER_DESCRIPTION_WORDS / TIER_GALLERY_MAX in backend routes/public.py
const TIER_PARAMS = {
  gold:   { label: 'Gold',       hint: 'Uncapped description · main photo + 9 gallery images · full contact details · top placement' },
  silver: { label: 'Silver',     hint: 'Up to 320 words · main photo + 4 gallery images · full contact details' },
  bronze: { label: 'Bronze',     hint: 'Up to 60 words · main photo + 1 gallery image · website + phone shown' },
  none:   { label: 'Non-member', hint: 'Name, town + 20-word summary only · main photo only · no phone shown' },
};

function TierChooser({ onPick, onClose }) {
  return (
    <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-center justify-center p-4" data-testid="tier-chooser-modal">
      <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-xl">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-display text-2xl">New listing — what membership level?</h3>
          <button onClick={onClose} className="p-2 hover:bg-stone rounded-lg" data-testid="tier-chooser-close"><X size={18}/></button>
        </div>
        <p className="text-sm text-muted mb-6">The level sets the listing&apos;s description length, photo allowance and placement. You can change it later in the editor.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {['gold', 'silver', 'bronze', 'none'].map((t) => (
            <button key={t} onClick={() => onPick(t === 'none' ? null : t)} data-testid={`tier-choose-${t}`}
              className="text-left border border-charcoal/15 rounded-2xl p-4 hover:border-heritage hover:bg-heritage/5 transition-colors">
              <div className="font-semibold mb-1 flex items-center gap-2">
                {t !== 'none' && <Sparkles size={13} className="text-mustard"/>}
                {TIER_PARAMS[t].label}
              </div>
              <div className="text-xs text-charcoal/60 leading-relaxed">{TIER_PARAMS[t].hint}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const ISSUE_LABELS = {
  broken: { label: 'BROKEN', cls: 'bg-heritage/15 text-heritage' },
  tiny: { label: 'TINY', cls: 'bg-mustard/25 text-charcoal' },
  small_file: { label: 'LOW-RES', cls: 'bg-mustard/25 text-charcoal' },
  unusual_shape: { label: 'ODD SHAPE', cls: 'bg-charcoal/10 text-charcoal' },
};

function PhotoQualityPanel({ onOpen }) {
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/admin/photo-quality/latest').then((r) => setReport(r.data.report)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (report?.status !== 'running') return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [report?.status]);
  const runNow = async () => {
    setBusy(true);
    try { await api.post('/admin/photo-quality/run'); setReport({ status: 'running' }); } catch (e) { /* noop */ }
    setBusy(false);
  };
  const flagged = report?.flagged || [];
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-6" data-testid="photo-quality-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => setOpen((s) => !s)} className="text-left" data-testid="photo-quality-toggle">
          <div className="text-sm font-semibold flex items-center gap-2">
            Photo quality
            {report?.status === 'complete' && report.flagged_total > 0 && (
              <span className="bg-heritage text-base text-[10px] font-bold rounded-full px-2 py-0.5">{report.flagged_total} flagged</span>
            )}
          </div>
          <div className="text-xs text-muted">
            {!report && 'Not run yet — scan every venue photo for broken links, tiny or stretched images.'}
            {report?.status === 'running' && 'Scanning photos… this takes a minute or two.'}
            {report?.status === 'complete' && (report.flagged_total === 0
              ? `All ${report.checked} photos look sharp · last checked ${new Date(report.ran_at).toLocaleDateString('en-GB')}`
              : `${report.flagged_total} of ${report.checked} photos need attention · last checked ${new Date(report.ran_at).toLocaleDateString('en-GB')}`)}
          </div>
        </button>
        <button onClick={runNow} disabled={busy || report?.status === 'running'} data-testid="photo-quality-run"
          className="border border-charcoal/15 hover:border-forest text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-50">
          {report?.status === 'running' ? 'Scanning…' : 'Check all photos now'}
        </button>
      </div>
      {open && report?.status === 'complete' && flagged.length > 0 && (
        <ul className="mt-4 border-t border-charcoal/10 pt-3 space-y-1.5" data-testid="photo-quality-list">
          {flagged.map((f, i) => (
            <li key={`${f.slug}-${f.field}-${i}`} className="flex items-center gap-3 text-sm">
              <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap ${(ISSUE_LABELS[f.issue] || {}).cls || 'bg-charcoal/10'}`}>
                {(ISSUE_LABELS[f.issue] || {}).label || f.issue}
              </span>
              <button onClick={() => onOpen(f.slug)} className="font-medium hover:text-heritage underline decoration-charcoal/20 text-left truncate" data-testid={`photo-quality-open-${f.slug}`}>
                {f.name}
              </button>
              <span className="text-xs text-muted whitespace-nowrap">{f.field}</span>
              <span className="text-xs text-muted truncate ml-auto">{f.detail}</span>
            </li>
          ))}
          <li className="text-xs text-muted pt-1">Open a venue and use Replace image / the Media Library to swap in a sharper photo, then re-run the check.</li>
        </ul>
      )}
    </div>
  );
}

function FreshnessPanel({ onOpen }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [digest, setDigest] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);
  const load = () => api.get('/admin/freshness/overview').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const stale = data.stale_listings || [];
  const due = data.partners_due_review || [];
  const runNow = async () => {
    setBusy(true);
    try { await api.post('/admin/freshness/run'); await load(); } catch (e) { /* noop */ }
    setBusy(false);
  };
  const toggleDigest = async () => {
    if (!digestOpen && !digest) {
      try { const r = await api.get('/admin/freshness/digest?days=30'); setDigest(r.data); } catch (e) { /* noop */ }
    }
    setDigestOpen((s) => !s);
  };
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-6" data-testid="freshness-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => setOpen((s) => !s)} className="text-left" data-testid="freshness-toggle">
          <div className="text-sm font-semibold flex items-center gap-2">
            Listing freshness
            {stale.length > 0 && <span className="bg-heritage text-base text-[10px] font-bold rounded-full px-2 py-0.5">{stale.length} stale</span>}
          </div>
          <div className="text-xs text-muted">
            {stale.length === 0 ? 'No listings older than 6 months — all fresh.' : `${stale.length} published listing${stale.length !== 1 ? 's' : ''} untouched for 6+ months`}
            {due.length > 0 && ` · ${due.length} partner${due.length !== 1 ? 's' : ''} due a quarterly review email`}
            {' · checks daily 7:45am'}
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button onClick={toggleDigest} data-testid="freshness-digest-toggle"
            className="border border-charcoal/15 hover:border-heritage text-xs font-semibold px-4 py-2 rounded-full">
            {digestOpen ? 'Hide digest' : 'Monthly digest'}
          </button>
          <button onClick={runNow} disabled={busy} data-testid="freshness-run-now"
            className="border border-charcoal/15 hover:border-forest text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-50">
            {busy ? 'Running…' : 'Run freshness cycle now'}
          </button>
        </div>
      </div>
      {digestOpen && digest && (
        <div className="mt-4 border-t border-charcoal/10 pt-3" data-testid="freshness-digest">
          <div className="text-xs font-semibold mb-2">
            Last 30 days: {digest.emailed} partner{digest.emailed !== 1 ? 's' : ''} emailed · {digest.updated} updated · <span className={digest.silent > 0 ? 'text-heritage' : ''}>{digest.silent} silent</span>
            <span className="text-charcoal/40 font-normal"> — auto-summary posted to Notifications on the 1st of each month</span>
          </div>
          {digest.emailed === 0 ? (
            <p className="text-xs text-muted">No review emails went out in the last 30 days.</p>
          ) : (
            <ul className="space-y-1.5">
              {digest.rows.map((r) => (
                <li key={r.partner_id} className="flex items-center gap-3 text-sm" data-testid={`digest-row-${r.partner_id}`}>
                  <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${r.updated_since_email ? 'bg-forest/15 text-forest' : 'bg-heritage/15 text-heritage'}`}>
                    {r.updated_since_email ? 'UPDATED' : 'SILENT'}
                  </span>
                  <span className="font-medium truncate">{r.business_name || r.email}</span>
                  <span className="text-xs text-muted truncate">{r.listing_name}</span>
                  <span className="text-xs text-muted ml-auto whitespace-nowrap">
                    emailed {r.emailed_at ? new Date(r.emailed_at).toLocaleDateString('en-GB') : '—'}
                    {!r.email_delivered && ' · delivery failed'}
                  </span>
                  {!r.updated_since_email && (
                    <a href={`mailto:${r.email}?subject=Your%20GoLeicestershire%20listing`} className="text-xs font-semibold text-heritage hover:underline whitespace-nowrap" data-testid={`digest-chase-${r.partner_id}`}>
                      Chase →
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {open && stale.length > 0 && (
        <ul className="mt-4 border-t border-charcoal/10 pt-3 space-y-1.5" data-testid="freshness-stale-list">
          {stale.map((a) => (
            <li key={a.slug} className="flex items-center gap-3 text-sm">
              <button onClick={() => onOpen(a)} className="font-medium hover:text-heritage underline decoration-charcoal/20 text-left" data-testid={`freshness-open-${a.slug}`}>
                {a.name || a.slug}
              </button>
              <span className="text-xs text-muted ml-auto whitespace-nowrap">
                last updated {a.updated_at ? new Date(a.updated_at).toLocaleDateString('en-GB') : 'unknown'}
                {a.member_tier ? ` · ${a.member_tier}` : ''}
              </span>
            </li>
          ))}
          <li className="text-xs text-muted pt-1">Opening and saving a listing refreshes it and clears the flag.</li>
        </ul>
      )}
    </div>
  );
}

function BreakSyncPanel({ onDraftCreated }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.get('/admin/breaks/sync/latest').then((r) => setRun(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy('sync');
    try { const r = await api.post('/admin/breaks/sync'); setRun(r.data); } catch (e) { /* noop */ }
    setBusy('');
  };
  const dismiss = async (s) => {
    setBusy(s);
    try { await api.post('/admin/breaks/sync/dismiss', { site_slug: s }); await load(); } catch (e) { /* noop */ }
    setBusy('');
  };
  const createDraft = async (s) => {
    setBusy(s);
    try {
      await api.post('/admin/breaks/sync/create-draft', { site_slug: s });
      await load();
      onDraftCreated?.();
    } catch (e) { /* noop */ }
    setBusy('');
  };
  if (!run) return null;
  const suggestions = run.suggested_new_venues || [];
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-6" data-testid="break-sync-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold">StayPlayExplore partner sync</div>
          <div className="text-xs text-muted">
            {run.ran_at ? `Last run ${new Date(run.ran_at).toLocaleString('en-GB')}` : 'Not run yet'}
            {typeof run.site_slugs_found === 'number' && ` · ${run.site_slugs_found} site listings · ${(run.changes || []).length} flag changes`}
            {' · runs every Monday 5:30am'}
          </div>
        </div>
        <button onClick={runNow} disabled={busy === 'sync'} data-testid="break-sync-run-now"
          className="border border-charcoal/15 hover:border-forest text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-50">
          {busy === 'sync' ? 'Syncing…' : 'Run sync now'}
        </button>
      </div>
      {(run.changes || []).length > 0 && (
        <div className="text-xs text-charcoal/70 mb-2" data-testid="break-sync-changes">
          Latest changes: {run.changes.map((c) => c.slug).join(', ')}
        </div>
      )}
      {suggestions.length > 0 ? (
        <div className="mt-3 border-t border-charcoal/10 pt-3" data-testid="break-sync-suggestions">
          <div className="text-xs font-semibold text-heritage uppercase tracking-wider mb-2">
            {suggestions.length} Leicestershire partner venue{suggestions.length !== 1 ? 's' : ''} on their site but missing from yours
          </div>
          <ul className="space-y-2">
            {suggestions.map((s) => (
              <li key={s.site_slug} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`break-sync-suggestion-${s.site_slug}`}>
                <a href={s.url} target="_blank" rel="noreferrer" className="font-medium hover:text-heritage underline decoration-charcoal/20">{s.name}</a>
                <span className="ml-auto flex items-center gap-2">
                  <button onClick={() => createDraft(s.site_slug)} disabled={busy === s.site_slug} data-testid={`break-sync-create-${s.site_slug}`}
                    className="bg-forest text-base text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-forest-deep disabled:opacity-50">
                    {busy === s.site_slug ? 'Working…' : 'Create draft listing'}
                  </button>
                  <button onClick={() => dismiss(s.site_slug)} disabled={busy === s.site_slug} data-testid={`break-sync-dismiss-${s.site_slug}`}
                    className="text-xs text-muted hover:text-heritage font-semibold px-2 py-1.5">
                    Dismiss
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        run.ran_at && (
          <div className="mt-2 text-xs text-charcoal/60" data-testid="break-sync-no-suggestions">
            No new venues to suggest — every Leicestershire venue on their site is already listed (or dismissed).
          </div>
        )
      )}
    </div>
  );
}

export default function AdminAttractions() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // attraction object or null
  const [creating, setCreating] = useState(false);
  const [dupWarn, setDupWarn] = useState([]);

  useEffect(() => {
    const name = editing?.name?.trim();
    if (!name || name.length < 4) { setDupWarn([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/admin/attractions-similar-names', {
          params: { name, exclude_slug: creating ? '' : (editing?.slug || '') } });
        setDupWarn(r.data.items || []);
      } catch { setDupWarn([]); }
    }, 500);
    return () => clearTimeout(t);
  }, [editing?.name]); // eslint-disable-line react-hooks/exhaustive-deps
  const [tierPrompt, setTierPrompt] = useState(false);
  const [photoIssues, setPhotoIssues] = useState({});

  useEffect(() => {
    api.get('/admin/photo-quality/latest').then((r) => {
      const map = {};
      ((r.data.report || {}).flagged || []).forEach((f) => {
        if (!map[f.slug]) map[f.slug] = `${f.field}: ${f.detail}`;
      });
      setPhotoIssues(map);
    }).catch(() => {});
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const FILTER_CONFIG = {
    'missing-open-days': {
      endpoint: '/admin/attractions/missing-open-days',
      title: 'attractions with no opening schedule',
      hint: 'Click any row to set its open days, season window and notes. The AI town narratives use this to skip venues that are closed today.',
    },
    'missing-postcode': {
      endpoint: '/admin/attractions/missing-postcode',
      title: 'attractions with no postcode',
      hint: 'Postcodes drive the map pins on town and city pages. Add the postcode and we\'ll auto-geocode it next time you save.',
    },
    'missing-photo': {
      endpoint: '/admin/attractions/missing-photo',
      title: 'attractions with no hero photo',
      hint: 'Hero images appear on the explore grid, town pages and homepage cards. Open the entry and use the Replace image button.',
    },
    'poor-quality': {
      endpoint: '/admin/attractions/poor-quality',
      title: 'attractions flagged for poor quality photos',
      hint: 'The photo quality audit flagged these images as tiny, broken or an unusual shape. Open the entry and replace the flagged image — the badge shows which one.',
    },
    'dwell-review': {
      endpoint: '/admin/attractions/dwell-review',
      title: 'attractions with a low-confidence dwell estimate',
      hint: 'Plan My Day uses these visit-time estimates to time the itinerary. Open each entry and confirm or tweak the number — that locks it in as a "manual" value so it\'ll never be overwritten by the AI estimator.',
    },
    'open-days-review': {
      endpoint: '/admin/attractions/open-days-review',
      title: 'attractions with a suspicious opening schedule',
      hint: 'These were auto-flagged because the open-days look unusual for the category (e.g. a heritage venue or pub marked open all 7 days, or a venue with only 1-2 open days). Open the entry, double-check against the venue\'s website, and adjust the Open days field. Saving clears the flag automatically.',
    },
    'unpublished': {
      endpoint: '/admin/attractions/unpublished',
      title: 'unpublished draft attractions',
      hint: 'These were auto-created — usually from the awards import. Add a hero photo, postcode, opening days and a fuller description, then tick "Published" to send them live. Trophy badges are already attached so they\'ll appear instantly.',
    },
  };
  const filterConfig = filter && FILTER_CONFIG[filter];

  // Guard against out-of-order responses: only the latest request may set items.
  const loadSeq = useRef(0);
  function load() {
    const seq = ++loadSeq.current;
    const apply = (r) => { if (seq === loadSeq.current) setItems(r.data.items); };
    if (filterConfig) {
      api.get(filterConfig.endpoint).then(apply);
      return;
    }
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    api.get(`/admin/attractions${q}`).then(apply);
  }
  useEffect(load, [search, filter]);

  // Persist the given editing payload. Extracted so the magic-autofill flow can
  // save the just-merged data without waiting for setEditing() to settle.
  async function persist(payload, { closeAfter = true, isCreate = creating } = {}) {
    payload = { ...payload };
    if (typeof payload.tags === 'string') {
      payload.tags = payload.tags.split(',').map((s) => s.trim()).filter(Boolean);
    }
    if (!payload.member_tier) payload.member_tier = null;
    if (isCreate) {
      if (!payload.slug) payload.slug = payload.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      await api.post('/admin/attractions', payload);
    } else {
      await api.patch(`/admin/attractions/${payload.slug}`, payload);
    }
    if (closeAfter) {
      setEditing(null);
      setCreating(false);
    }
    load();
  }

  async function save() {
    try {
      await persist(editing);
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
  }

  // Typed-name confirm before any soft-delete — nothing lands in Trash by accident.
  const [deleteTarget, setDeleteTarget] = useState(null); // {slug, name}
  const [deleteTyped, setDeleteTyped] = useState('');
  function del(slug, name) {
    setDeleteTyped('');
    setDeleteTarget({ slug, name });
  }
  async function confirmDelete() {
    const t = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/admin/attractions/${t.slug}`);
      loadTrash();
      loadBlocklist();
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Delete failed — please try again.');
    }
  }

  // ---- Trash (soft-delete) management ----
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  function loadTrash() {
    api.get('/admin/attractions/trash').then((r) => setTrash(r.data.items || [])).catch(() => {});
  }
  useEffect(loadTrash, []);
  const [blocklist, setBlocklist] = useState([]);
  const [showBlocklist, setShowBlocklist] = useState(false);
  function loadBlocklist() {
    api.get('/admin/attractions/blocklist').then((r) => setBlocklist(r.data.items || [])).catch(() => {});
  }
  useEffect(loadBlocklist, []);
  async function unblockVenue(nameNorm) {
    try {
      await api.delete(`/admin/attractions/blocklist/${nameNorm}`);
      loadBlocklist();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not unblock.');
    }
  }
  async function restoreFromTrash(slug) {
    await api.post(`/admin/attractions/${slug}/restore`);
    loadTrash();
    load();
  }
  async function purgeFromTrash(slug, name) {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone — the venue's data will be wiped from the database forever.`)) return;
    if (!window.confirm('Are you absolutely sure? Type-check: this venue will be GONE.')) return;
    await api.delete(`/admin/attractions/${slug}/purge`);
    loadTrash();
  }

  // One-click "I just typed a name, fill in the rest" — chains Find Website
  // and AI-fill-from-website so adding a new venue feels like one keystroke.
  const [magicFilling, setMagicFilling] = useState(false);
  async function magicAutoFill() {
    if (!editing?.name?.trim()) return alert('Type the venue name first.');
    setMagicFilling(true);
    try {
      // Step 1: derive a slug + town early so the backend can act on this venue
      const draftSlug = editing.slug || editing.name.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
      let workingEditing = { ...editing, slug: draftSlug };

      // If we're creating a brand-new attraction, save a minimal stub FIRST so
      // the per-slug endpoints have something to look up. Status is published=false
      // so the half-finished venue doesn't leak to the public.
      if (creating) {
        try {
          await api.post('/admin/attractions', {
            ...workingEditing,
            published: false,
          });
        } catch (e) {
          if (e.response?.status !== 409) throw e;  // 409 = slug already exists, fine
        }
      }
      setEditing(workingEditing);

      // Step 2: Find website (skip if admin already typed one)
      let websiteUrl = workingEditing.website?.trim() || '';
      if (!websiteUrl) {
        try {
          const r = await api.post(`/admin/attractions/${draftSlug}/find-website`);
          if (r.data?.url) websiteUrl = r.data.url;
        } catch (_) { /* non-fatal */ }
      }
      if (!websiteUrl) {
        websiteUrl = window.prompt(
          `Couldn't auto-find a website for "${workingEditing.name}". Paste the URL if you have it (or leave blank to write copy from the name alone):`,
          ''
        ) || '';
      }

      // Step 3: AI-fill description + postcode + open_days
      const filled = [];
      const r2 = await api.post(`/admin/attractions/${draftSlug}/ai-fill-description`,
        websiteUrl ? { website: websiteUrl } : {});
      const next = { ...workingEditing, website: websiteUrl || workingEditing.website };
      if (r2.data.summary) { next.summary = r2.data.summary; filled.push('summary'); }
      if (r2.data.description) { next.description = r2.data.description; filled.push('description'); }
      if (r2.data.postcode && !next.postcode) { next.postcode = r2.data.postcode; filled.push(`postcode ${r2.data.postcode}`); }
      if (r2.data.open_days?.length && !(next.open_days?.length)) {
        next.open_days = r2.data.open_days;
        filled.push(`opening days (${r2.data.open_days.join(', ')})`);
      }
      if (r2.data.email && !(next.email || '').trim()) { next.email = r2.data.email; filled.push(`email ${r2.data.email}`); }
      if (r2.data.phone && !(next.phone || '').trim()) { next.phone = r2.data.phone; filled.push(`phone ${r2.data.phone}`); }
      if (r2.data.tripadvisor_url && !(next.tripadvisor_url || '').trim()) { next.tripadvisor_url = r2.data.tripadvisor_url; filled.push('TripAdvisor link'); }
      if (r2.data.hero_image && !(next.hero_image || '').trim()) { next.hero_image = r2.data.hero_image; filled.push('suggested photo (from their website)'); }
      setEditing(next);

      if (filled.length === 0) {
        alert('Nothing extracted — the website was unreachable or had no readable info. Fill in the fields manually.');
        return;
      }

      // Offer to save and close in one shot when the fill produced a confident,
      // publishable result. We require the four ingredients that make a listing
      // genuinely useful: a description, a postcode (drives geocoding +
      // distance-aware filters) and a known location. The admin can still
      // decline to review first.
      const looksPublishable =
        next.description && next.summary && next.postcode &&
        next.open_days?.length;
      if (looksPublishable && window.confirm(
        `Auto-filled:\n  • ${filled.join('\n  • ')}\n\n` +
        `Save and close now? Click Cancel to review the fields first (recommended for the first few you try).`
      )) {
        try {
          // Use the explicit `next` payload — React's setEditing() above hasn't
          // committed yet, so reading from `editing` here would persist stale data.
          // We also pass `isCreate: false` because step 1 already inserted the
          // draft; we want a PATCH this time.
          await persist(next, { closeAfter: true, isCreate: false });
        } catch (e) {
          alert(e.response?.data?.detail || e.message || 'Save failed — pre-fill kept, please save manually.');
        }
      } else {
        alert(`Auto-filled:\n  • ${filled.join('\n  • ')}\n\nReview and click Save when happy. Lat/lng will geocode automatically from the postcode on save.`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Auto-fill failed');
    } finally {
      setMagicFilling(false);
    }
  }

  async function aiCopy() {
    if (!editing?.name) return alert('Add a name first');
    const r = await api.post('/admin/ai/copy', { venue_name: editing.name, notes: editing.description || editing.summary || '', tone: 'playful' });
    setEditing({ ...editing, description: r.data.text, summary: r.data.text.split('. ').slice(0, 2).join('. ') + '.' });
  }

  // AI-fill from the venue's own website — used for draft stubs auto-created
  // by the awards scanner. We DO NOT auto-save; the suggestion goes into the
  // form fields and the admin reviews + clicks the existing Save.
  const [aiFilling, setAiFilling] = useState(false);
  // Web-search for the venue's official site (DuckDuckGo behind the scenes)
  const [findingWebsite, setFindingWebsite] = useState(false);
  async function findWebsite() {
    if (!editing?.slug) return alert('Save the attraction first so it has a slug, then try again.');
    setFindingWebsite(true);
    try {
      const r = await api.post(`/admin/attractions/${editing.slug}/find-website`);
      if (!r.data.ok || !r.data.url) {
        const others = r.data.candidates?.map((c) => c.url).slice(0, 3).join('\n  • ') || '(none)';
        alert(`Couldn't confidently pick a website. Top candidates:\n  • ${others}\n\nPaste one of these into the Website field if it looks right.`);
        return;
      }
      // Pre-fill, but let the admin see + edit before doing anything else
      setEditing({ ...editing, website: r.data.url });
      if (r.data.note) {
        alert(`Suggested website: ${r.data.url}\n\n${r.data.note}`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Search failed');
    } finally {
      setFindingWebsite(false);
    }
  }

  async function aiFillFromWebsite() {
    if (!editing?.slug) return alert('Save the attraction first so it has a slug, then try again.');
    const overrideUrl = editing.website?.trim() || window.prompt(
      `Enter the venue's website URL for "${editing.name}" (we'll fetch a description from it). Leave blank to write from the name alone:`,
      ''
    );
    if (overrideUrl === null) return;  // cancelled
    setAiFilling(true);
    try {
      const r = await api.post(`/admin/attractions/${editing.slug}/ai-fill-description`,
        overrideUrl ? { website: overrideUrl } : {});
      const hasFacts = r.data.email || r.data.phone || r.data.tripadvisor_url || r.data.hero_image || r.data.postcode || (r.data.open_days || []).length;
      if (!r.data.summary && !r.data.description && !hasFacts) {
        alert('Couldn\'t pull anything useful from that URL — try a different page on the same site, or fill the description in manually.');
        return;
      }
      // Auto-fill missing details — never overwrite existing admin values.
      // Collect what changed so we can show a single toast at the end.
      const filled = [];
      const nextPostcode = (!editing.postcode || (editing.postcode + '').trim() === '') && r.data.postcode
        ? r.data.postcode
        : editing.postcode;
      if (nextPostcode && nextPostcode !== editing.postcode) filled.push(`postcode ${nextPostcode}`);

      const hasOpenDays = Array.isArray(editing.open_days) && editing.open_days.length > 0;
      const nextOpenDays = !hasOpenDays && Array.isArray(r.data.open_days) && r.data.open_days.length > 0
        ? r.data.open_days
        : (editing.open_days || []);
      if (nextOpenDays !== editing.open_days && nextOpenDays.length) filled.push(`opening days (${nextOpenDays.join(', ')})`);

      const fillIfEmpty = (field, value, label) => {
        if (value && (!editing[field] || (editing[field] + '').trim() === '')) {
          filled.push(`${label}: ${value.length > 60 ? value.slice(0, 57) + '…' : value}`);
          return value;
        }
        return editing[field];
      };
      const nextEmail = fillIfEmpty('email', r.data.email, 'email');
      const nextPhone = fillIfEmpty('phone', r.data.phone, 'phone');
      const nextTripadvisor = fillIfEmpty('tripadvisor_url', r.data.tripadvisor_url, 'TripAdvisor');
      const nextHero = fillIfEmpty('hero_image', r.data.hero_image, 'suggested photo (from their website)');

      setEditing({
        ...editing,
        summary: r.data.summary || editing.summary,
        description: r.data.description || editing.description,
        website: overrideUrl || editing.website,
        postcode: nextPostcode,
        open_days: nextOpenDays,
        email: nextEmail,
        phone: nextPhone,
        tripadvisor_url: nextTripadvisor,
        hero_image: nextHero,
      });
      if (filled.length) {
        alert(`Picked up from the website:\n  • ${filled.join('\n  • ')}\n\nReview before saving.`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'AI-fill failed');
    } finally {
      setAiFilling(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label text-heritage mb-2">Catalogue</div>
          <h1 className="font-display text-4xl">Attractions <span className="text-charcoal/40">({items.length})</span></h1>
        </div>
        <div className="flex items-center gap-3">
          <input value={search} onChange={(e) => { setSearch(e.target.value); if (filter) setSearchParams({}); }} placeholder="Search..." data-testid="admin-attractions-search"
            className="bg-white border border-charcoal/15 rounded-full px-5 py-2.5 text-sm w-64 focus:outline-none focus:border-forest" />
          <button onClick={() => setTierPrompt(true)} data-testid="admin-attraction-new"
            className="bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep">
            <Plus size={14}/> New
          </button>
        </div>
      </div>

      <HelpCallout
        title="The heart of the site — every venue, day out and stay in Leicestershire."
        description="Each row here is a public-facing attraction page. Changes save instantly and appear across the site as soon as you tick 'Published'. Use the AI rewrite button inside the editor to refresh stale copy in a friendly, motivating tone."
        appearsAt={[
          { label: '/explore grid', where: 'filtered by category, town, and the 7 lifestyle flags' },
          { label: '/explore/<slug>', where: 'the full detail page with gallery, contact, map and TripAdvisor link' },
          { label: 'Homepage "Standout days out" grid', where: 'features attractions where "Featured member" is ticked' },
          { label: '/towns/<slug>', where: 'attractions matching the town name appear in that town\'s page' },
          { label: '🏆 Trophy badge', where: 'shown on cards + detail when this attraction has won a Tourism Award' },
        ]}
      />

      <BreakSyncPanel onDraftCreated={load} />

      <FreshnessPanel onOpen={(a) => { setEditing({ ...a }); setCreating(false); }} />

      <PhotoQualityPanel onOpen={(slug) => {
        const a = items.find((x) => x.slug === slug);
        if (a) { setEditing({ ...a }); setCreating(false); } else { setSearch(slug); }
      }} />

      {deleteTarget && (
        <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-2">Move &ldquo;{deleteTarget.name}&rdquo; to Trash?</h3>
            <p className="text-sm text-charcoal/70 mb-4">
              The listing comes off the public site immediately. You can restore it from the Trash for 90 days.
              To confirm, type the venue name exactly:
            </p>
            <input autoFocus value={deleteTyped} onChange={(e) => setDeleteTyped(e.target.value)}
              placeholder={deleteTarget.name}
              data-testid="delete-confirm-input"
              className="w-full bg-base border border-charcoal/15 rounded-xl px-4 py-2.5 text-sm mb-4 focus:outline-none focus:border-heritage" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} data-testid="delete-confirm-cancel"
                className="px-4 py-2 rounded-full text-sm font-semibold hover:bg-stone">Cancel</button>
              <button onClick={confirmDelete}
                disabled={deleteTyped.trim().toLowerCase() !== deleteTarget.name.trim().toLowerCase()}
                data-testid="delete-confirm-submit"
                className="px-4 py-2 rounded-full text-sm font-semibold bg-heritage text-cream disabled:opacity-40 disabled:cursor-not-allowed hover:bg-heritage/90">
                Move to Trash
              </button>
            </div>
          </div>
        </div>
      )}

      {tierPrompt && (
        <TierChooser
          onClose={() => setTierPrompt(false)}
          onPick={(tier) => {
            setEditing({ ...emptyAttraction(), member_tier: tier, is_featured_member: !!tier });
            setCreating(true);
            setTierPrompt(false);
          }}
        />
      )}

      {filterConfig && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 flex items-start gap-3" data-testid="filter-banner">
          <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Showing {items.length} {filterConfig.title}</div>
            <div className="text-sm text-amber-800 mt-1">{filterConfig.hint}</div>
          </div>
          <button
            onClick={() => { setSearchParams({}); }}
            data-testid="filter-clear"
            className="text-sm font-semibold text-amber-900 underline whitespace-nowrap"
          >
            Show all
          </button>
        </div>
      )}

      {/* Trash — soft-deleted venues. Always loaded so the count badge is live. */}
      <div className="mb-6" data-testid="trash-panel">
        <button
          onClick={() => setShowTrash((s) => !s)}
          data-testid="toggle-trash"
          className="w-full text-left bg-stone/60 border border-charcoal/10 rounded-xl px-5 py-3 flex items-center justify-between hover:bg-stone transition-colors"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold">
            <Archive size={15} />
            Trash <span className="text-charcoal/50 font-normal">({trash.length})</span>
          </span>
          <span className="text-xs text-muted">
            {trash.length === 0 ? 'No soft-deleted venues' : (showTrash ? 'Click to hide' : 'Click to view and restore')}
          </span>
        </button>
        {showTrash && trash.length > 0 && (
          <div className="mt-2 bg-white border border-charcoal/10 rounded-xl divide-y divide-charcoal/5" data-testid="trash-list">
            {trash.map((t) => (
              <div key={t.slug} className="px-5 py-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-xs text-muted truncate">
                    <code>{t.slug}</code>
                    {t.town && <span> · {t.town}</span>}
                    {t.deleted_at && <span> · deleted {new Date(t.deleted_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>}
                    {t.deleted_by && <span> by {t.deleted_by}</span>}
                  </div>
                </div>
                <button
                  onClick={() => restoreFromTrash(t.slug)}
                  data-testid={`restore-${t.slug}`}
                  className="text-xs px-3 py-1.5 rounded-md border border-forest text-forest hover:bg-forest hover:text-white inline-flex items-center gap-1 transition-colors"
                >
                  <RotateCcw size={12} /> Restore
                </button>
                <button
                  onClick={() => purgeFromTrash(t.slug, t.name)}
                  data-testid={`purge-${t.slug}`}
                  className="text-xs px-3 py-1.5 rounded-md border border-red-500/40 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Delete forever
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Blocked venues — deleted drafts that discovery must never re-create */}
      <div className="mb-6" data-testid="blocklist-panel">
        <button
          onClick={() => setShowBlocklist((s) => !s)}
          data-testid="toggle-blocklist"
          className="w-full text-left bg-stone/60 border border-charcoal/10 rounded-xl px-5 py-3 flex items-center justify-between hover:bg-stone transition-colors"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold">
            <Ban size={15} />
            Blocked venues <span className="text-charcoal/50 font-normal">({blocklist.length})</span>
          </span>
          <span className="text-xs text-muted">
            {blocklist.length === 0 ? 'No rejected venues — deleted drafts appear here' : (showBlocklist ? 'Click to hide' : 'Click to view and unblock')}
          </span>
        </button>
        {showBlocklist && blocklist.length > 0 && (
          <div className="mt-2 bg-white border border-charcoal/10 rounded-xl divide-y divide-charcoal/5" data-testid="blocklist-list">
            {blocklist.map((b) => (
              <div key={b.name_norm} className="px-5 py-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{b.name}</div>
                  <div className="text-xs text-muted truncate">
                    Auto-discovery will never re-create this venue
                    {b.blocked_at && <span> · blocked {new Date(b.blocked_at).toLocaleString('en-GB', { dateStyle: 'medium' })}</span>}
                    {b.blocked_by && <span> by {b.blocked_by}</span>}
                  </div>
                </div>
                <button
                  onClick={() => unblockVenue(b.name_norm)}
                  data-testid={`unblock-${b.name_norm}`}
                  className="text-xs px-3 py-1.5 rounded-md border border-forest text-forest hover:bg-forest hover:text-white inline-flex items-center gap-1 transition-colors"
                >
                  <RotateCcw size={12} /> Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden">
        {/* Desktop: full table. Mobile: card list with visible Edit/Delete buttons. */}
        <table className="hidden md:table w-full text-sm">
          <thead className="bg-admin text-charcoal/60 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4 font-semibold">Name</th>
              <th className="text-left p-4 font-semibold">Category</th>
              <th className="text-left p-4 font-semibold">Town</th>
              <th className="text-left p-4 font-semibold">Member</th>
              <th className="text-left p-4 font-semibold">Published</th>
              <th className="text-right p-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id || a.slug}
                  onClick={() => { if (a.deleted_at) return; setEditing({ ...a }); setCreating(false); }}
                  className="border-t border-charcoal/5 hover:bg-admin/50 cursor-pointer"
                  data-testid={`admin-attraction-row-${a.slug}`}>
                <td className="p-4">
                  <div className="font-semibold flex items-center gap-2">
                    {a.name}
                    {a.deleted_at && (
                      <span data-testid={`in-trash-${a.slug}`} title="This venue was deleted — it's in the Trash. Restore it to bring it back." className="bg-charcoal text-cream text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full whitespace-nowrap">In Trash</span>
                    )}
                    {!a.hero_image && (
                      <span data-testid={`no-image-${a.slug}`} title="This listing has no main photo at all" className="bg-heritage text-cream text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full whitespace-nowrap">No image</span>
                    )}
                    {a.hero_image && (a.hero_image.includes('images.unsplash.com') || a.hero_image.includes('placeholder')) && (
                      <span data-testid={`needs-photo-${a.slug}`} title="Hero image is a stock placeholder — replace with the venue's own photo" className="bg-mustard text-charcoal text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Stock photo</span>
                    )}
                    {a.hero_image && photoIssues[a.slug] && (
                      <span data-testid={`low-quality-${a.slug}`} title={photoIssues[a.slug]} className="bg-mustard/60 text-charcoal text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Poor quality image</span>
                    )}
                  </div>
                  <div className="text-xs text-muted">{a.slug}</div>
                </td>
                <td className="p-4 text-muted">{CATEGORY_LABELS[a.category] || a.category}</td>
                <td className="p-4 text-muted">{a.town || '—'}</td>
                <td className="p-4">{a.is_featured_member ? <span className="inline-flex items-center gap-1 text-mustard font-semibold"><Sparkles size={12}/>{a.member_tier}</span> : '—'}</td>
                <td className="p-4">{a.published ? <span className="text-forest font-semibold">●</span> : <span className="text-muted">○</span>}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  {a.deleted_at ? (<>
                    <button onClick={async (e) => { e.stopPropagation(); await restoreFromTrash(a.slug); load(); }}
                      className="px-3 py-1.5 rounded-full bg-forest text-cream text-xs font-semibold hover:bg-forest-deep"
                      data-testid={`restore-inline-${a.slug}`}>
                      Restore
                    </button>
                    <button onClick={async (e) => { e.stopPropagation(); await purgeFromTrash(a.slug, a.name); load(); }}
                      className="p-2 rounded-lg ml-1 hover:bg-stone text-heritage"
                      data-testid={`purge-inline-${a.slug}`}
                      aria-label="Delete forever" title="Delete forever (cannot be undone)">
                      <Trash2 size={14}/>
                    </button>
                  </>) : (<>
                  <button onClick={(e) => { e.stopPropagation(); setEditing({ ...a }); setCreating(false); }} className="p-2 hover:bg-stone rounded-lg" data-testid={`edit-${a.slug}`} aria-label="Edit"><Edit2 size={14}/></button>
                  <button onClick={(e) => { e.stopPropagation(); del(a.slug, a.name); }}
                    className="p-2 rounded-lg ml-1 hover:bg-stone text-heritage transition-colors"
                    data-testid={`delete-${a.slug}`}
                    aria-label="Delete" title="Move to Trash">
                    <Trash2 size={14}/>
                  </button>
                  </>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile card list */}
        <ul className="md:hidden divide-y divide-charcoal/5" data-testid="admin-attractions-mobile-list">
          {items.map((a) => (
            <li
              key={a.id || a.slug}
              onClick={() => { if (a.deleted_at) return; setEditing({ ...a }); setCreating(false); }}
              className="p-4 active:bg-admin/60 cursor-pointer"
              data-testid={`admin-attraction-card-${a.slug}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {a.name}
                    {a.deleted_at && (
                      <span data-testid={`in-trash-mobile-${a.slug}`} className="bg-charcoal text-cream text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full whitespace-nowrap">In Trash</span>
                    )}
                    {a.is_featured_member && (
                      <span className="inline-flex items-center gap-1 text-mustard font-semibold text-[10px] uppercase tracking-wider">
                        <Sparkles size={10}/>{a.member_tier}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {CATEGORY_LABELS[a.category] || a.category} · {a.town || '—'}{a.published ? '' : ' · draft'}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {a.deleted_at ? (<>
                    <button
                      onClick={async (e) => { e.stopPropagation(); await restoreFromTrash(a.slug); load(); }}
                      className="px-3 py-2 rounded-xl bg-forest text-cream text-xs font-semibold"
                      data-testid={`restore-mobile-${a.slug}`}
                    >
                      Restore
                    </button>
                    <button
                      onClick={async (e) => { e.stopPropagation(); await purgeFromTrash(a.slug, a.name); load(); }}
                      className="p-2.5 rounded-xl bg-red-50 text-red-600"
                      data-testid={`purge-mobile-${a.slug}`}
                      aria-label={`Delete ${a.name} forever`}
                      title="Delete forever (cannot be undone)"
                    >
                      <Trash2 size={16}/>
                    </button>
                  </>) : (<>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditing({ ...a }); setCreating(false); }}
                    className="p-2.5 bg-admin rounded-xl"
                    data-testid={`edit-mobile-${a.slug}`}
                    aria-label={`Edit ${a.name}`}
                  >
                    <Edit2 size={16}/>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); del(a.slug, a.name); }}
                    className="p-2.5 rounded-xl bg-red-50 text-red-600"
                    data-testid={`delete-mobile-${a.slug}`}
                    aria-label={`Delete ${a.name}`}
                  >
                    <Trash2 size={16}/>
                  </button>
                  </>)}
                </div>
              </div>
            </li>
          ))}
          {items.length === 0 && (
            <li className="p-8 text-center text-muted text-sm">No attractions to show.</li>
          )}
        </ul>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h3 className="font-display text-2xl">{creating ? 'New attraction' : editing.name}</h3>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="p-2 hover:bg-stone rounded-lg" data-testid="edit-close"><X size={18}/></button>
            </div>
            <div className="p-6 space-y-5">
              {/* One-click auto-fill: chains Find Website → AI-fill. Most
                  useful for new venues where you've only typed a name. */}
              <button
                type="button"
                onClick={magicAutoFill}
                disabled={magicFilling || !editing.name?.trim()}
                data-testid="edit-magic-autofill"
                className="w-full bg-heritage text-base px-5 py-3.5 rounded-2xl font-semibold inline-flex items-center justify-center gap-2 hover:bg-heritage/90 disabled:opacity-50 transition-colors shadow-soft"
                title="Find the website, then auto-fill summary, description, postcode and opening days"
              >
                <Sparkles size={16}/>
                {magicFilling ? 'Finding website + auto-filling…' : (creating ? '✨ Auto-fill everything from the name' : '✨ Top up missing fields from the venue website')}
              </button>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <div className="label mb-1.5">Name</div>
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid="edit-name"/>
                  {dupWarn.length > 0 && (
                    <div className="mt-1.5 p-2.5 rounded-xl bg-mustard/20 border border-mustard/50 text-xs text-charcoal flex items-start gap-1.5" data-testid="duplicate-name-warning">
                      <AlertTriangle size={13} className="shrink-0 mt-0.5 text-heritage"/>
                      <span>
                        <strong>Possible duplicate:</strong>{' '}
                        {dupWarn.map((d, i) => (
                          <span key={d.slug}>{i > 0 && '; '}"{d.name}" ({d.trashed ? 'in Trash' : d.published ? 'published' : 'draft'})</span>
                        ))}
                        {' '}— check before saving so renames don't create hidden twins.
                      </span>
                    </div>
                  )}
                </label>
                <label className="block">
                  <div className="label mb-1.5">Slug</div>
                  <input value={editing.slug} disabled={!creating} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5 disabled:opacity-60"/>
                </label>
                <label className="block">
                  <div className="label mb-1.5">Category</div>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid="edit-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <div className="label mb-1.5">Town</div>
                  <input value={editing.town || ''} onChange={(e) => setEditing({ ...editing, town: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block md:col-span-2">
                  <div className="label mb-1.5">Street address</div>
                  <input
                    value={editing.address || ''}
                    onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                    placeholder="e.g. Sutton Lane, Sutton Cheney"
                    className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
                    data-testid="edit-address"
                  />
                </label>
                <label className="block">
                  <div className="label mb-1.5 flex items-center justify-between">
                    <span>Postcode</span>
                    {!editing.postcode && <span className="text-xs text-amber-700 font-semibold">drives map pins</span>}
                  </div>
                  <input
                    value={editing.postcode || ''}
                    onChange={(e) => setEditing({ ...editing, postcode: e.target.value.toUpperCase() })}
                    placeholder="CV13 0AD"
                    className={`w-full bg-admin border rounded-xl px-4 py-2.5 ${editing.postcode ? 'border-charcoal/15' : 'border-amber-300'}`}
                    data-testid="edit-postcode"
                  />
                </label>
              </div>
              <ImageUploadField
                value={editing.hero_image}
                onChange={(v) => setEditing({ ...editing, hero_image: v })}
                label="Hero image"
                testidPrefix="att-hero"
              />
              <label className="block">
                <div className="label mb-1.5">Summary <span className="text-charcoal/40 font-normal normal-case">— the FIRST text visitors see (top of the page + cards). If you rewrite the description, refresh this too or the old wording stays on show</span></div>
                <input value={editing.summary || ''} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
              </label>
              <label className="block">
                <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                  <div className="label">Description</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={aiFillFromWebsite}
                      type="button"
                      disabled={aiFilling}
                      data-testid="edit-ai-fill-website"
                      className="text-xs bg-heritage text-base px-3 py-1.5 rounded-full inline-flex items-center gap-1 hover:bg-heritage/90 disabled:opacity-60"
                      title="Fetch the venue website and write a summary + description"
                    >
                      <Sparkles size={12}/> {aiFilling ? 'Reading website…' : 'AI-fill from website'}
                    </button>
                    <button onClick={aiCopy} type="button" data-testid="edit-ai-copy" className="text-xs bg-charcoal text-base px-3 py-1.5 rounded-full inline-flex items-center gap-1 hover:bg-charcoal/90">
                      <Sparkles size={12}/> AI rewrite
                    </button>
                  </div>
                </div>
                <textarea rows={6} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
              </label>
              <div className="grid grid-cols-3 gap-4">
                <label className="block">
                  <div className="label mb-1.5">Phone</div>
                  <input value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
                </label>
                <label className="block">
                  <div className="label mb-1.5">Email</div>
                  <input value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
                </label>
                <label className="block">
                  <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                    <div className="label">Website</div>
                    <button
                      type="button"
                      onClick={findWebsite}
                      disabled={findingWebsite}
                      data-testid="edit-find-website"
                      className="text-xs bg-heritage/15 text-heritage hover:bg-heritage/25 px-2.5 py-1 rounded-full inline-flex items-center gap-1 disabled:opacity-60 font-semibold"
                      title="Search the web for the venue's official site"
                    >
                      <Sparkles size={11}/> {findingWebsite ? 'Searching…' : 'Find website'}
                    </button>
                  </div>
                  <input value={editing.website || ''} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid="edit-website"/>
                </label>
              </div>
              <label className="block">
                <div className="label mb-1.5">TripAdvisor URL</div>
                <input value={editing.tripadvisor_url || ''} onChange={(e) => setEditing({ ...editing, tripadvisor_url: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" placeholder="https://www.tripadvisor.com/..."/>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['family_friendly', 'dog_friendly', 'free_entry', 'indoor', 'outdoor', 'accessible', 'evening_friendly'].map((k) => (
                  <label key={k} className="flex items-center gap-2 bg-admin border border-charcoal/10 rounded-xl px-4 py-3 cursor-pointer">
                    <input type="checkbox" checked={!!editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.checked })} data-testid={`edit-flag-${k}`}/>
                    <span className="text-sm font-medium">{k.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-3 bg-admin border border-charcoal/10 rounded-xl px-4 py-3">
                  <input type="checkbox" checked={!!editing.is_featured_member} onChange={(e) => setEditing({ ...editing, is_featured_member: e.target.checked })} data-testid="edit-featured-member"/>
                  <span className="text-sm font-semibold">Featured member</span>
                </label>
                <label className="block">
                  <div className="label mb-1.5">Member tier</div>
                  <select value={editing.member_tier || ''} onChange={(e) => setEditing({ ...editing, member_tier: e.target.value || null })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid="edit-member-tier">
                    {TIERS.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                  </select>
                  <p className="text-xs text-charcoal/60 mt-1.5" data-testid="edit-tier-hint">
                    {TIER_PARAMS[editing.member_tier || 'none']?.hint}
                  </p>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-3 bg-mustard/10 border border-mustard/40 rounded-xl px-4 py-3 cursor-pointer">
                  <input type="checkbox" checked={!!editing.stayplayexplore} onChange={(e) => setEditing({ ...editing, stayplayexplore: e.target.checked })} data-testid="edit-flag-stayplayexplore"/>
                  <span className="text-sm font-semibold">Stay Play Explore partner <span className="font-normal text-muted">(family breaks)</span></span>
                </label>
                <label className="flex items-center gap-3 bg-heritage/10 border border-heritage/30 rounded-xl px-4 py-3 cursor-pointer">
                  <input type="checkbox" checked={!!editing.perfectbreak} onChange={(e) => setEditing({ ...editing, perfectbreak: e.target.checked })} data-testid="edit-flag-perfectbreak"/>
                  <span className="text-sm font-semibold">Perfect Break partner <span className="font-normal text-muted">(adults only)</span></span>
                </label>
              </div>
              <label className="flex items-center gap-3 bg-admin border border-charcoal/10 rounded-xl px-4 py-3 cursor-pointer">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })}/>
                <span className="text-sm font-semibold">Published (visible on the site)</span>
              </label>

              {/* Opening schedule — drives the "open today" filter for the AI town narratives */}
              <div className="border-t border-charcoal/10 pt-5">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <div className="label">Opening days</div>
                  {editing.open_days_needs_review && (
                    <span className="text-xs text-heritage inline-flex items-center gap-1.5 bg-heritage/10 border border-heritage/30 px-2.5 py-1 rounded-full" data-testid="open-days-review-badge">
                      <AlertTriangle size={12}/> Needs review{editing.open_days_review_reason ? ` — ${editing.open_days_review_reason}` : ''}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted mb-3">Used by the AI town narrative generator to skip venues that are closed today. Leave empty if open every day or unknown.</p>
                <div className="grid grid-cols-7 gap-2">
                  {['mon','tue','wed','thu','fri','sat','sun'].map((d) => {
                    const days = Array.isArray(editing.open_days) ? editing.open_days : [];
                    const checked = days.includes(d);
                    return (
                      <label key={d} className={`flex flex-col items-center gap-1 border rounded-xl px-2 py-2 cursor-pointer ${checked ? 'bg-forest text-base border-forest' : 'bg-admin border-charcoal/15'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked ? [...days, d] : days.filter((x) => x !== d);
                            setEditing({ ...editing, open_days: next });
                          }}
                          data-testid={`edit-open-day-${d}`}
                          className="sr-only"
                        />
                        <span className="text-xs font-semibold uppercase">{d}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <label className="block">
                    <div className="label mb-1.5">Season open from (month)</div>
                    <select
                      value={editing.season_open_from || ''}
                      onChange={(e) => setEditing({ ...editing, season_open_from: e.target.value ? parseInt(e.target.value, 10) : null })}
                      className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
                      data-testid="edit-season-from"
                    >
                      <option value="">Year-round</option>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <div className="label mb-1.5">Season open to (month)</div>
                    <select
                      value={editing.season_open_to || ''}
                      onChange={(e) => setEditing({ ...editing, season_open_to: e.target.value ? parseInt(e.target.value, 10) : null })}
                      className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
                      data-testid="edit-season-to"
                    >
                      <option value="">Year-round</option>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block mt-3">
                  <div className="label mb-1.5">Opening notes (optional)</div>
                  <input
                    value={editing.opening_notes || ''}
                    onChange={(e) => setEditing({ ...editing, opening_notes: e.target.value })}
                    placeholder="e.g. Sundays + bank holidays only, last entry 4pm"
                    className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
                    data-testid="edit-opening-notes"
                  />
                </label>
              </div>

              {/* Dwell time — drives Plan My Day scheduling */}
              <div className="bg-stone/40 border border-charcoal/10 rounded-2xl p-5">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="label">Realistic visit time</div>
                  {editing.dwell_source && (
                    <span
                      data-testid="edit-dwell-source"
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${
                        editing.dwell_source === 'manual' ? 'bg-forest/15 text-forest'
                          : editing.dwell_source === 'ai_estimate' ? 'bg-heritage/15 text-heritage'
                          : 'bg-charcoal/10 text-charcoal/70'
                      }`}
                    >
                      {editing.dwell_source === 'manual' ? 'Manually set' : editing.dwell_source === 'ai_estimate' ? `AI · ${editing.dwell_confidence || 'medium'} confidence` : 'Category default'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted mb-3">
                  How long a typical visit takes, in minutes. Plan My Day uses this to time the itinerary.
                  Editing this locks the value as &quot;manual&quot; — the automatic estimator will never overwrite it.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="number"
                    min="15"
                    max="360"
                    step="15"
                    value={editing.dwell_minutes ?? ''}
                    onChange={(e) => setEditing({
                      ...editing,
                      dwell_minutes: e.target.value === '' ? null : parseInt(e.target.value, 10),
                    })}
                    placeholder="e.g. 90"
                    className="bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5 w-32"
                    data-testid="edit-dwell-minutes"
                  />
                  <span className="text-sm text-muted">minutes</span>
                  {editing.dwell_needs_review && (
                    <span className="text-xs text-heritage inline-flex items-center gap-1.5">
                      <AlertTriangle size={12}/> Needs review
                    </span>
                  )}
                </div>
              </div>

              {/* Accessibility — Phase 2 of the accessibility roadmap.
                  Three-state controls (Yes / No / Unknown) instead of plain
                  checkboxes because "unknown" must be distinguishable from
                  "confirmed no" — claiming a venue has NO hearing loop when
                  we just don't know is exactly the mistake that ruins a
                  disabled visitor's day. */}
              {(() => {
                const acc = editing.accessibility || {};
                const setAcc = (patch) => setEditing({ ...editing, accessibility: { ...acc, ...patch } });
                const triProps = (field) => ({
                  field,
                  value: acc[field],
                  onChange: (v) => setAcc({ [field]: v }),
                });
                return (
                  <div className="bg-stone/40 border border-charcoal/10 rounded-2xl p-5" data-testid="edit-accessibility">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="label">Accessibility</div>
                      {acc.last_verified_at && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold bg-forest/15 text-forest" data-testid="acc-verified-badge">
                          Verified {new Date(acc.last_verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted mb-4">
                      What disabled visitors can expect at this venue. <strong>Use the &quot;?&quot;
                      button when you&apos;re not sure</strong> — &quot;No&quot; should mean confirmed-no,
                      not guessed-no.
                    </p>

                    {/* Mobility group */}
                    <div className="bg-white rounded-xl px-4 py-2 mb-3">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-heritage py-2">Mobility</div>
                      <AccessTriToggle {...triProps('step_free_entrance')} label="Step-free entrance" />
                      <AccessTriToggle {...triProps('step_free_between_floors')} label="Step-free between floors" hint="Lift, ramp, or single-level layout." />
                      <AccessTriToggle {...triProps('accessible_parking')} label="Accessible parking" />
                      <AccessTriToggle {...triProps('accessible_toilet')} label="Accessible toilet" />
                      <AccessTriToggle {...triProps('changing_places_toilet')} label="Changing Places toilet" hint="The fully-equipped adult changing facility — gold standard." />
                      <AccessTriToggle {...triProps('wheelchair_loan')} label="Wheelchair available to borrow" />
                    </div>

                    {/* Sensory & service group */}
                    <div className="bg-white rounded-xl px-4 py-2 mb-3">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-heritage py-2">Sensory &amp; service</div>
                      <AccessTriToggle {...triProps('hearing_loop')} label="Hearing loop" />
                      <AccessTriToggle {...triProps('large_print_available')} label="Large-print materials available" />
                      <AccessTriToggle {...triProps('sensory_friendly_sessions')} label="Sensory-friendly sessions" hint="Quiet hour, autism-friendly times, relaxed performances." />
                      <AccessTriToggle {...triProps('assistance_dogs_welcome')} label="Assistance dogs welcome" />
                      <AccessTriToggle {...triProps('carer_concession')} label="Carer goes free / discount for carer" />
                    </div>

                    {/* Notes + external guides */}
                    <label className="text-sm block mb-3">
                      <div className="font-medium text-charcoal mb-1">Notes</div>
                      <textarea
                        rows="3"
                        value={acc.notes || ''}
                        onChange={(e) => setAcc({ notes: e.target.value })}
                        placeholder="e.g. Quiet hour first Sunday of each month, 9-10am. Café has step-free access via side door — ask staff."
                        className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
                        data-testid="acc-notes"
                      />
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <label className="text-sm">
                        <div className="font-medium text-charcoal mb-1">Venue&apos;s own access guide URL</div>
                        <input
                          type="url"
                          value={acc.own_access_guide_url || ''}
                          onChange={(e) => setAcc({ own_access_guide_url: e.target.value })}
                          placeholder="https://venue.co.uk/accessibility.pdf"
                          className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"
                          data-testid="acc-own-url"
                        />
                      </label>
                      <label className="text-sm">
                        <div className="font-medium text-charcoal mb-1">AccessAble guide URL</div>
                        <input
                          type="url"
                          value={acc.accessable_url || ''}
                          onChange={(e) => setAcc({ accessable_url: e.target.value, accessable_link_auto: false })}
                          placeholder="https://www.accessable.co.uk/..."
                          className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"
                          data-testid="acc-accessable-url"
                        />
                        {acc.accessable_link_auto && (
                          <div className="text-[10px] text-muted mt-1">Auto-generated AccessAble search URL. Replace with the venue&apos;s direct guide URL when you find it.</div>
                        )}
                      </label>
                      <label className="text-sm">
                        <div className="font-medium text-charcoal mb-1">Euan&apos;s Guide URL</div>
                        <input
                          type="url"
                          value={acc.euans_guide_url || ''}
                          onChange={(e) => setAcc({ euans_guide_url: e.target.value, euans_link_auto: false })}
                          placeholder="https://www.euansguide.com/..."
                          className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"
                          data-testid="acc-euans-url"
                        />
                        {acc.euans_link_auto && (
                          <div className="text-[10px] text-muted mt-1">Auto-matched from Euan&apos;s Guide sitemap. Replace with a venue-confirmed URL if this match is wrong.</div>
                        )}
                      </label>
                    </div>

                    <div className="mt-4 flex items-center justify-between flex-wrap gap-2 text-xs text-muted">
                      <div>
                        Source: <strong className="text-charcoal/80">{acc.source || 'not set'}</strong>
                        {acc.last_verified_at && (
                          <> · last verified {new Date(acc.last_verified_at).toLocaleDateString('en-GB')}</>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setAcc({
                          last_verified_at: new Date().toISOString(),
                          source: 'venue',
                        })}
                        className="bg-forest text-base px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-forest-deep"
                        data-testid="acc-mark-verified"
                      >
                        Mark verified now
                      </button>
                    </div>
                  </div>
                );
              })()}


              {/* Past awards — historical winners pre-platform.
                  Platform-cycle wins (status: "winner") are managed in /admin/awards
                  and shown read-only here. Manual entries get status: "historic". */}
              <div className="bg-stone/40 border border-charcoal/10 rounded-2xl p-5">
                <div className="label mb-2">Past awards</div>
                <p className="text-xs text-muted mb-3">
                  Add historical / pre-platform wins manually. Trophy badges show on the public site automatically.
                </p>
                <div className="space-y-2 mb-3">
                  {(editing.awards_won || []).length === 0 && (
                    <div className="text-xs text-muted italic">No past awards listed.</div>
                  )}
                  {(editing.awards_won || []).map((a, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`edit-award-row-${i}`}>
                      <input
                        type="number"
                        min="1900"
                        max="2100"
                        value={a.year || ''}
                        onChange={(e) => {
                          const next = [...(editing.awards_won || [])];
                          next[i] = { ...next[i], year: e.target.value ? parseInt(e.target.value, 10) : null };
                          setEditing({ ...editing, awards_won: next });
                        }}
                        placeholder="Year"
                        className="w-24 bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"
                      />
                      <input
                        value={a.category || ''}
                        onChange={(e) => {
                          const next = [...(editing.awards_won || [])];
                          next[i] = { ...next[i], category: e.target.value };
                          setEditing({ ...editing, awards_won: next });
                        }}
                        placeholder="Category / award name (e.g. Best Family Attraction)"
                        className="flex-1 min-w-[200px] bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"
                      />
                      {a.status === 'winner' ? (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-mustard/30 text-charcoal/80 font-semibold">Platform vote</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-charcoal/10 text-charcoal/70 font-semibold">Historic</span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const next = [...(editing.awards_won || [])];
                          next.splice(i, 1);
                          setEditing({ ...editing, awards_won: next });
                        }}
                        disabled={a.status === 'winner'}
                        title={a.status === 'winner' ? 'Platform-vote awards are managed in /admin/awards' : 'Remove this entry'}
                        className="p-2 text-heritage hover:bg-heritage/5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`edit-award-delete-${i}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setEditing({
                    ...editing,
                    awards_won: [...(editing.awards_won || []), { year: new Date().getFullYear() - 1, category: '', status: 'historic' }],
                  })}
                  data-testid="edit-award-add"
                  className="text-xs font-semibold text-heritage hover:underline inline-flex items-center gap-1"
                >
                  <Plus size={12}/> Add past award
                </button>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="px-5 py-2.5 rounded-full font-semibold border border-charcoal/15">Cancel</button>
              <button onClick={save} data-testid="edit-save" className="bg-forest text-base px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-forest-deep"><Save size={14}/> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
